import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import {
  ARROW_DOWN_KEY,
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  ARROW_UP_KEY,
  ENTER_KEY,
  TAB_KEY
} from "constants/hotKeys.enums";

/**
 * Focus zones that form the keyboard-navigation circuit.
 * Consumers declare which zones are active; the hook cycles through them
 * in the order they appear in the array.
 */
export type FocusZone = "searchbar" | "category" | "toolbar" | "tabs" | "fields";

interface UseFieldPickerKeyboardOptions {
  /** Ordered list of zones to cycle through. */
  zones: FocusZone[];
  /** When false, all hotkeys are disabled. */
  isEnabled: boolean;
  /** Called when Enter is pressed in the "category" zone. */
  onCategorySelect?: (index: number) => void;
  /** Called when Enter is pressed in the "fields" zone. */
  onFieldSelect?: (index: number) => void;
}

export interface FieldPickerKeyboard {
  focusedList: FocusZone | "none";
  focusedIndex: number;
  /** Ref arrays — consumers wire these to their list items via onItemRef. */
  categoryRefs: React.MutableRefObject<Maybe<HTMLElement>[]>;
  toolbarRefs: React.MutableRefObject<Maybe<HTMLElement>[]>;
  tabRefs: React.MutableRefObject<Maybe<HTMLElement>[]>;
  fieldRefs: React.MutableRefObject<Maybe<HTMLElement>[]>;
  /** Callback for wiring refs from list-item render. */
  setCategoryRef: (index: number, el: Maybe<HTMLElement>) => void;
  setToolbarRef: (index: number, el: Maybe<HTMLElement>) => void;
  setTabRef: (index: number, el: Maybe<HTMLElement>) => void;
  setFieldRef: (index: number, el: Maybe<HTMLElement>) => void;
  /** Cycle to the next populated zone (Tab behavior). Exposed for consumers
   *  whose Tab key is intercepted before reaching the document-level hotkey. */
  tabCycle: () => void;
}

export function useFieldPickerKeyboard({
  zones,
  isEnabled,
  onCategorySelect,
  onFieldSelect
}: UseFieldPickerKeyboardOptions): FieldPickerKeyboard {
  const [focusedList, setFocusedList] = useState<FocusZone | "none">("none");
  const [focusedIndex, setFocusedIndex] = useState(0);

  const categoryRefs = useRef<Maybe<HTMLElement>[]>([]);
  const toolbarRefs = useRef<Maybe<HTMLElement>[]>([]);
  const tabRefs = useRef<Maybe<HTMLElement>[]>([]);
  const fieldRefs = useRef<Maybe<HTMLElement>[]>([]);

  // Reset focus when the picker becomes enabled (opened)
  useEffect(() => {
    if (isEnabled && zones.length > 0) {
      setFocusedList(zones[0]);
      setFocusedIndex(0);
    }
  }, [isEnabled]);

  // Scroll focused element into view
  useEffect(() => {
    if (!isEnabled) return;
    const refs = getRefsForZone(focusedList);
    const el = refs?.current[focusedIndex];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "auto", block: "nearest" });
    }
  }, [focusedIndex, focusedList, isEnabled]);

  function getRefsForZone(zone: FocusZone | "none") {
    switch (zone) {
      case "category":
        return categoryRefs;
      case "toolbar":
        return toolbarRefs;
      case "tabs":
        return tabRefs;
      case "fields":
        return fieldRefs;
      default:
        return null;
    }
  }

  function getItemCount(zone: FocusZone | "none") {
    const refs = getRefsForZone(zone);
    if (!refs) return 0;
    return refs.current.filter(Boolean).length;
  }

  /** True if the DOM element is present and not disabled. */
  function isRefEnabled(el: Maybe<HTMLElement>): boolean {
    if (!el) return false;
    if ("disabled" in el && (el as HTMLButtonElement).disabled) return false;
    if (el.getAttribute && el.getAttribute("aria-disabled") === "true") return false;
    return true;
  }

  /** Indices of enabled refs in a zone, in order. */
  function getEnabledIndices(zone: FocusZone | "none"): number[] {
    const refs = getRefsForZone(zone);
    if (!refs) return [];
    return refs.current
      .map((r, i) => (isRefEnabled(r) ? i : null))
      .filter((i): i is number => i !== null);
  }

  function nextZone(current: FocusZone | "none"): FocusZone | null {
    const idx = zones.indexOf(current as FocusZone);
    if (idx === -1 || idx === zones.length - 1) return null;
    return zones[idx + 1];
  }

  function prevZone(current: FocusZone | "none"): FocusZone | null {
    const idx = zones.indexOf(current as FocusZone);
    if (idx <= 0) return null;
    return zones[idx - 1];
  }

  function navigate(direction: "up" | "down" | "left" | "right") {
    if (direction === "right") {
      // Walk forward through zones, skipping unpopulated ones
      let target = nextZone(focusedList);
      while (target && !isZonePopulated(target)) {
        target = nextZone(target);
      }
      if (target) {
        setFocusedList(target);
        setFocusedIndex(firstEnabledIndex(target));
      }
    } else if (direction === "left") {
      let target = prevZone(focusedList);
      while (target && !isZonePopulated(target)) {
        target = prevZone(target);
      }
      if (target) {
        setFocusedList(target);
        setFocusedIndex(firstEnabledIndex(target));
      }
    } else {
      // up / down within the current zone
      const refs = getRefsForZone(focusedList);
      if (!refs) return;

      if (focusedList === "fields") {
        // Fields have sparse refs (group headers create gaps)
        const available = refs.current
          .map((r, i) => (r ? i : null))
          .filter((i): i is number => i !== null);
        if (available.length === 0) return;
        const pos = available.indexOf(focusedIndex);
        const inc = direction === "down" ? 1 : -1;
        const next = pos === -1 ? 0 : (pos + inc + available.length) % available.length;
        setFocusedIndex(available[next]);
      } else if (focusedList === "toolbar") {
        // Toolbar items can be disabled; cycle through enabled ones only
        const available = getEnabledIndices(focusedList);
        if (available.length === 0) return;
        const pos = available.indexOf(focusedIndex);
        const inc = direction === "down" ? 1 : -1;
        const next = pos === -1 ? 0 : (pos + inc + available.length) % available.length;
        setFocusedIndex(available[next]);
      } else {
        const count = refs.current.filter(Boolean).length;
        if (count === 0) return;
        const inc = direction === "down" ? 1 : -1;
        setFocusedIndex((focusedIndex + inc + count) % count);
      }
    }
  }

  /** Index to land on when entering a zone via left/right navigation. */
  function firstEnabledIndex(zone: FocusZone): number {
    if (zone === "toolbar") {
      const enabled = getEnabledIndices(zone);
      return enabled[0] ?? 0;
    }
    return 0;
  }

  function selectItem() {
    if (focusedList === "category") {
      onCategorySelect?.(focusedIndex);
      // After selecting a category, advance focus to the next zone
      navigate("right");
      return;
    } else if (focusedList === "fields") {
      onFieldSelect?.(focusedIndex);
    } else {
      const refs = getRefsForZone(focusedList);
      const el = refs?.current[focusedIndex];
      el?.click();
    }
  }

  function isZonePopulated(zone: FocusZone) {
    // searchbar always reachable (single input, no tracked refs).
    // toolbar items register asynchronously: if no refs yet, treat as
    // reachable; if refs exist but all are disabled, skip the zone.
    if (zone === "searchbar") return true;
    if (zone === "toolbar") {
      const total = getItemCount(zone);
      return total === 0 || getEnabledIndices(zone).length > 0;
    }
    return getItemCount(zone) > 0;
  }

  function tabCycle() {
    const idx = zones.indexOf(focusedList as FocusZone);
    for (let i = 1; i < zones.length; i++) {
      const candidate = zones[(idx + i) % zones.length];
      if (isZonePopulated(candidate)) {
        setFocusedList(candidate);
        setFocusedIndex(firstEnabledIndex(candidate));
        return;
      }
    }
    // All other zones are empty — stay put
  }

  // --- Hotkey bindings ---
  const hotkeyOpts = { enabled: isEnabled, enableOnFormTags: true as const };
  useHotkeys(ARROW_UP_KEY, () => navigate("up"), hotkeyOpts);
  useHotkeys(ARROW_DOWN_KEY, () => navigate("down"), hotkeyOpts);
  useHotkeys(ARROW_LEFT_KEY, () => navigate("left"), hotkeyOpts);
  useHotkeys(ARROW_RIGHT_KEY, () => navigate("right"), hotkeyOpts);
  useHotkeys(ENTER_KEY, () => selectItem(), hotkeyOpts);
  useHotkeys(
    TAB_KEY,
    (e) => {
      e.preventDefault();
      tabCycle();
    },
    hotkeyOpts
  );

  const setCategoryRef = useCallback((index: number, el: Maybe<HTMLElement>) => {
    categoryRefs.current[index] = el;
  }, []);
  const setToolbarRef = useCallback((index: number, el: Maybe<HTMLElement>) => {
    toolbarRefs.current[index] = el;
  }, []);
  const setTabRef = useCallback((index: number, el: Maybe<HTMLElement>) => {
    tabRefs.current[index] = el;
  }, []);
  const setFieldRef = useCallback((index: number, el: Maybe<HTMLElement>) => {
    fieldRefs.current[index] = el;
  }, []);

  return {
    focusedList,
    focusedIndex,
    categoryRefs,
    toolbarRefs,
    tabRefs,
    fieldRefs,
    setCategoryRef,
    setToolbarRef,
    setTabRef,
    setFieldRef,
    tabCycle
  };
}
